import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import AddressForm from '../components/AddressForm';
import PaymentMethodForm from '../components/PaymentMethodForm';
import {
  updateUserProfile,
  getUserOrders,
  getUserAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
  getUserPaymentMethods,
  addPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  updateUserPassword
} from '../services/firestoreService';

// Brand colors
const CREAM_COLOR = '#fff';
const BLACK_COLOR = '#000000';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0 80px;
  min-height: 80vh;
  background-color: ${CREAM_COLOR};
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    padding: 40px 0 60px;
  }
`;

const WelcomeHeader = styled.div`
  width: 90%;
  max-width: 900px;
  margin: 0 auto 50px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 92%;
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    width: 94%;
    margin-bottom: 30px;
  }
`;

const Title = styled.h1`
  font-family: 'CursiveNice', 'Helvetica Neue', sans-serif;
  font-size: 52px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${BLACK_COLOR};
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 42px;
    margin-bottom: 14px;
  }
  
  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 12px;
  }
`;

const Subtitle = styled.p`
  font-family: 'Helvetica Neue', sans-serif;
  font-size: 20px;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const TilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 90%;
  max-width: 1000px;
  margin: 0 auto 48px;
  
  @media (max-width: 768px) {
    width: 92%;
    gap: 28px;
    margin-bottom: 36px;
  }
  
  @media (max-width: 480px) {
    width: 94%;
  }
`;

const Tile = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 36px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  min-height: 260px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: auto;
  width: 100%;
  margin: 0 auto;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 
      0 5px 15px rgba(0, 0, 0, 0.1),
      0 3px 3px rgba(0, 0, 0, 0.05),
      inset 0 -3px 0 rgba(0, 0, 0, 0.02);
  }
  
  /* Accent color strip at top */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background-color: ${BLACK_COLOR};
  }
  
  @media (max-width: 768px) {
    padding: 30px 26px;
    min-height: auto;
    border-radius: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 26px 22px;
    border-radius: 12px;
  }
`;

const TileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding-bottom: 20px;
  margin-bottom: 28px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding-bottom: 18px;
    margin-bottom: 24px;
  }
  
  @media (max-width: 480px) {
    padding-bottom: 16px;
    margin-bottom: 20px;
    gap: 12px;
  }
`;

const TileIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${CREAM_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);
  
  svg {
    width: 24px;
    height: 24px;
    color: ${BLACK_COLOR};
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const TileTitle = styled.h2`
  font-family: 'Helvetica Neue', sans-serif;
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  color: ${BLACK_COLOR};
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const TileContent = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0;
  text-align: center;
  max-width: 600px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const InfoText = styled.p`
  font-family: 'Helvetica Neue', sans-serif;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 28px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  
  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 22px;
  }
`;

const Button = styled.button`
  padding: 14px 28px;
  font-size: 16px;
  font-weight: bold;
  color: ${CREAM_COLOR};
  background-color: ${BLACK_COLOR};
  border: 2px solid ${BLACK_COLOR};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 32px;
  display: block;
  margin-left: auto;
  margin-right: auto;
  min-width: 180px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    color: ${BLACK_COLOR};
    background-color: ${CREAM_COLOR};
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 15px;
    margin-top: 24px;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
    padding: 14px 20px;
    text-align: center;
    margin-top: 24px;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 28px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const ItemActionsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
  width: 100%;

  ${props => props.$alignRight && `
    margin-left: auto;
    width: auto;
    justify-content: flex-end;
    flex-wrap: wrap;
    > * { flex: 0 0 auto; }
  `}

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    margin-left: 0;
  }
`;

const CompactButton = styled(Button)`
  padding: 10px 16px;
  font-size: 14px;
  min-width: 0;
  margin-top: 0;
  flex: 1 1 0;

  @media (max-width: 480px) {
    width: 100%;
  }

  ${props => props.$danger && `
    background-color: #f44336;
    border-color: #f44336;
    color: #ffffff;

    &:hover {
      background-color: ${CREAM_COLOR};
      color: #f44336;
      border-color: #f44336;
    }
  `}
`;

const FormGroup = styled.div`
  margin-bottom: 28px;
  text-align: left;
  width: 100%;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    margin-bottom: 22px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${BLACK_COLOR};
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  
  &:focus {
    border-color: ${BLACK_COLOR};
    outline: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    font-size: 15px;
  }
`;

const OrderItem = styled.div`
  padding: 18px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    padding: 14px 10px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const OrderDetails = styled.div`
  flex: 1;
`;

const OrderNumber = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
`;

const OrderDate = styled.div`
  font-size: 14px;
  color: #777;
`;

const OrderStatus = styled.div`
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  background-color: ${props => {
    switch(props.status) {
      case 'delivered': return '#e6f7e6';
      case 'shipped': return '#e6f0ff';
      case 'processing': return '#fff8e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'delivered': return '#2e7d32';
      case 'shipped': return '#1565c0';
      case 'processing': return '#ed6c02';
      default: return '#757575';
    }
  }};
  
  @media (max-width: 480px) {
    margin-top: 8px;
  }
`;

const Address = styled.div`
  padding: 18px;
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 18px;
  position: relative;
  
  ${props => props.default && `
    border-color: ${BLACK_COLOR};
    background-color: rgba(0, 0, 0, 0.04);
    
    &::after {
      content: 'Default';
      position: absolute;
      top: 10px;
      right: 10px;
      font-size: 12px;
      padding: 3px 8px;
      background-color: ${CREAM_COLOR};
      color: ${BLACK_COLOR};
      border-radius: 10px;
      font-weight: 500;
    }
  `}
  
  @media (max-width: 480px) {
    padding: 14px;
  }
`;

const PaymentMethod = styled.div`
  display: flex;
  align-items: center;
  padding: 18px;
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 12px;
  
  ${props => props.default && `
    border-color: ${BLACK_COLOR};
    background-color: rgba(0, 0, 0, 0.04);
  `}
  
  @media (max-width: 480px) {
    padding: 14px;
    flex-wrap: wrap;
  }
`;

const CardIcon = styled.div`
  width: 50px;
  height: 30px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
  color: #555;
`;

const CardDetails = styled.div`
  flex: 1;
`;

const CardNumber = styled.div`
  font-weight: 500;
`;

const CardExpiry = styled.div`
  font-size: 14px;
  color: #777;
`;

const LoadingIndicator = styled.div`
  padding: 15px;
  text-align: center;
  color: #555;
  font-style: italic;
`;

const LogoutButton = styled(Button)`
  background-color: transparent;
  color: ${BLACK_COLOR};
  border: 2px solid ${BLACK_COLOR};
  
  &:hover {
    background-color: ${CREAM_COLOR};
    color: ${BLACK_COLOR};
  }
`;

const ErrorMessage = styled.div`
  padding: 10px 15px;
  margin-bottom: 15px;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: 14px;
`;

// Mock data removed - now using real Firebase data

const Account = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  // State for real Firebase data
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState({
    profile: false,
    orders: false,
    addresses: false,
    payment: false
  });
  const [error, setError] = useState({
    profile: null,
    orders: null,
    addresses: null,
    payment: null,
    security: null
  });
  
  // Modal states
  const [addressModal, setAddressModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [profileForm, setProfileForm] = useState({
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Fetch all data once on mount (horizontal tiles show all sections)
  useEffect(() => {
    if (!currentUser?.uid) return;

    // Initialize profile form from current user
    setProfileForm({
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || ''
    });

    const fetchAll = async () => {
      // Orders
      try {
        setLoading(prev => ({ ...prev, orders: true }));
        const userOrders = await getUserOrders(currentUser.uid);
        setOrders(userOrders);
        setError(prev => ({ ...prev, orders: null }));
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(prev => ({ ...prev, orders: 'Failed to load orders. Please try again.' }));
      } finally {
        setLoading(prev => ({ ...prev, orders: false }));
      }

      // Addresses
      try {
        setLoading(prev => ({ ...prev, addresses: true }));
        const userAddresses = await getUserAddresses(currentUser.uid);
        setAddresses(userAddresses);
        setError(prev => ({ ...prev, addresses: null }));
      } catch (err) {
        console.error('Error fetching addresses:', err);
        // Don't show error if it's just that no addresses exist yet
        if (err.message && err.message.includes('No addresses found')) {
          setAddresses([]);
          setError(prev => ({ ...prev, addresses: null }));
        } else {
          setError(prev => ({ ...prev, addresses: 'Failed to load addresses. Please try again.' }));
        }
      } finally {
        setLoading(prev => ({ ...prev, addresses: false }));
      }

      // Payment Methods
      try {
        setLoading(prev => ({ ...prev, payment: true }));
        const userPaymentMethods = await getUserPaymentMethods(currentUser.uid);
        setPaymentMethods(userPaymentMethods);
        setError(prev => ({ ...prev, payment: null }));
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        // Don't show error if it's just that no payment methods exist yet
        if (err.message && err.message.includes('No payment methods found')) {
          setPaymentMethods([]);
          setError(prev => ({ ...prev, payment: null }));
        } else {
          setError(prev => ({ ...prev, payment: 'Failed to load payment methods. Please try again.' }));
        }
      } finally {
        setLoading(prev => ({ ...prev, payment: false }));
      }
    };

    fetchAll();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };
  
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) return;
    
    try {
      setLoading(prev => ({ ...prev, profile: true }));
      await updateUserProfile(currentUser.uid, profileForm);
      setError(prev => ({ ...prev, profile: null }));
      alert('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(prev => ({ ...prev, profile: 'Failed to update profile. Please try again.' }));
    } finally {
      setLoading(prev => ({ ...prev, profile: false }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) return;
    
    // Validate passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError(prev => ({ ...prev, security: 'New passwords do not match.' }));
      return;
    }
    
    try {
      setLoading(prev => ({ ...prev, security: true }));
      // Note: In a real implementation, you would use Firebase Auth to update the password
      // This is a placeholder for the actual implementation
      await updateUserPassword(currentUser.uid, passwordForm.currentPassword, passwordForm.newPassword);
      setError(prev => ({ ...prev, security: null }));
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      alert('Password updated successfully!');
    } catch (err) {
      console.error('Error updating password:', err);
      setError(prev => ({ ...prev, security: 'Failed to update password. Please check your current password and try again.' }));
    } finally {
      setLoading(prev => ({ ...prev, security: false }));
    }
  };

  

  // Icons for tiles
  const ProfileIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
  
  const OrdersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  );
  
  const AddressIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
  
  const PaymentIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
  
  const SecurityIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );

  return (
    <Container>
      <WelcomeHeader>
        <Title>For The Good Guys</Title>
        <Subtitle>Welcome, {currentUser?.firstName || 'User'}</Subtitle>
      </WelcomeHeader>
      
      <TilesContainer>
        {/* Profile Tile */}
        <Tile>
          <TileHeader>
            <TileIcon>
              <ProfileIcon />
            </TileIcon>
            <TileTitle>Profile</TileTitle>
          </TileHeader>
          <TileContent>
            <InfoText>
              Manage your personal information and account preferences here.
            </InfoText>
            {error.profile && <ErrorMessage>{error.profile}</ErrorMessage>}
            {currentUser && (
              <form onSubmit={handleProfileSubmit}>
                <FormGroup>
                  <Label>First Name</Label>
                  <Input 
                    type="text" 
                    name="firstName"
                    value={profileForm.firstName}
                    onChange={handleProfileChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Last Name</Label>
                  <Input 
                    type="text" 
                    name="lastName"
                    value={profileForm.lastName}
                    onChange={handleProfileChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email</Label>
                  <Input type="email" value={currentUser.email} readOnly />
                </FormGroup>
                <ActionsRow>
                  <Button type="submit" disabled={loading.profile} style={{ margin: 0 }}>
                    {loading.profile ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <LogoutButton type="button" onClick={handleLogout} style={{ margin: 0 }}>
                    Log Out
                  </LogoutButton>
                </ActionsRow>
              </form>
            )}
          </TileContent>
        </Tile>
        
        {/* Orders Tile */}
        <Tile>
          <TileHeader>
            <TileIcon>
              <OrdersIcon />
            </TileIcon>
            <TileTitle>Orders</TileTitle>
          </TileHeader>
          <TileContent>
            <InfoText>
              View and track your order history.
            </InfoText>
            {error.orders && <ErrorMessage>{error.orders}</ErrorMessage>}
            
            {loading.orders ? (
              <LoadingIndicator>Loading your orders...</LoadingIndicator>
            ) : orders.length > 0 ? (
              orders.map(order => (
                <OrderItem key={order.id}>
                  <OrderDetails>
                    <OrderNumber>Order #{order.id.substring(0, 6).toUpperCase()}</OrderNumber>
                    <OrderDate>
                      {order.createdAt instanceof Date 
                        ? order.createdAt.toLocaleDateString() 
                        : (order.createdAt?.seconds ? new Date(order.createdAt.seconds * 1000).toLocaleDateString() : '')}
                      {' • $'}
                      {typeof order.total === 'number' ? order.total.toFixed(2) : '0.00'}
                    </OrderDate>
                  </OrderDetails>
                  <OrderStatus status={order.status || 'processing'}>
                    {(order.status || 'processing').charAt(0).toUpperCase() + (order.status || 'processing').slice(1)}
                  </OrderStatus>
                </OrderItem>
              ))
            ) : (
              <InfoText>You haven't placed any orders yet.</InfoText>
            )}
            <Button onClick={() => navigate('/checkout')}>Place New Order</Button>
          </TileContent>
        </Tile>
        
        {/* Addresses Tile */}
        <Tile>
          <TileHeader>
            <TileIcon>
              <AddressIcon />
            </TileIcon>
            <TileTitle>Addresses</TileTitle>
          </TileHeader>
          <TileContent>
            <InfoText>
              Manage your shipping and billing addresses.
            </InfoText>
            {error.addresses && <ErrorMessage>{error.addresses}</ErrorMessage>}
            
            {loading.addresses ? (
              <LoadingIndicator>Loading your addresses...</LoadingIndicator>
            ) : addresses.length > 0 ? (
              addresses.map(address => (
                <Address key={address.id} default={address.default}>
                  <strong>{address.name}</strong><br />
                  {address.street}<br />
                  {address.city}, {address.state} {address.zip}
                  <ItemActionsRow>
                    <CompactButton
                      onClick={() => {
                        setEditingAddress(address);
                        setAddressModal(true);
                      }}
                    >
                      Edit
                    </CompactButton>
                    <CompactButton
                      $danger
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this address?')) {
                          try {
                            await deleteAddress(currentUser.uid, address.id);
                            // Refresh addresses
                            const updatedAddresses = await getUserAddresses(currentUser.uid);
                            setAddresses(updatedAddresses);
                          } catch (err) {
                            console.error('Error deleting address:', err);
                            setError(prev => ({ ...prev, addresses: 'Failed to delete address. Please try again.' }));
                          }
                        }
                      }}
                    >
                      Delete
                    </CompactButton>
                  </ItemActionsRow>
                </Address>
              ))
            ) : (
              <InfoText>You haven't added any addresses yet.</InfoText>
            )}
            <Button
              onClick={() => {
                setEditingAddress(null);
                setAddressModal(true);
              }}
            >
              Add New Address
            </Button>
          </TileContent>
        </Tile>
        
        {/* Payment Methods Tile */}
        <Tile>
          <TileHeader>
            <TileIcon>
              <PaymentIcon />
            </TileIcon>
            <TileTitle>Payment Methods</TileTitle>
          </TileHeader>
          <TileContent>
            <InfoText>
              Manage your payment methods.
            </InfoText>
            {error.payment && <ErrorMessage>{error.payment}</ErrorMessage>}
            
            {loading.payment ? (
              <LoadingIndicator>Loading your payment methods...</LoadingIndicator>
            ) : paymentMethods.length > 0 ? (
              paymentMethods.map(method => (
                <PaymentMethod key={method.id} default={method.default}>
                  <CardIcon>{method.type}</CardIcon>
                  <CardDetails>
                    <CardNumber>{method.type} •••• {method.last4}</CardNumber>
                    <CardExpiry>Expires {method.expiry}</CardExpiry>
                  </CardDetails>
                  <ItemActionsRow $alignRight>
                    <CompactButton
                      onClick={() => {
                        setEditingPayment(method);
                        setPaymentModal(true);
                      }}
                    >
                      Edit
                    </CompactButton>
                    <CompactButton
                      $danger
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this payment method?')) {
                          try {
                            await deletePaymentMethod(currentUser.uid, method.id);
                            // Refresh payment methods
                            const updatedPaymentMethods = await getUserPaymentMethods(currentUser.uid);
                            setPaymentMethods(updatedPaymentMethods);
                          } catch (err) {
                            console.error('Error deleting payment method:', err);
                            setError(prev => ({ ...prev, payment: 'Failed to delete payment method. Please try again.' }));
                          }
                        }
                      }}
                    >
                      Delete
                    </CompactButton>
                  </ItemActionsRow>
                </PaymentMethod>
              ))
            ) : (
              <InfoText>You haven't added any payment methods yet.</InfoText>
            )}
            <Button
              onClick={() => {
                setEditingPayment(null);
                setPaymentModal(true);
              }}
            >
              Add New Payment Method
            </Button>
          </TileContent>
        </Tile>
        
        {/* Security Tile */}
        <Tile>
          <TileHeader>
            <TileIcon>
              <SecurityIcon />
            </TileIcon>
            <TileTitle>Security</TileTitle>
          </TileHeader>
          <TileContent>
            <InfoText>
              Update your password and security settings.
            </InfoText>
            {error.security && <ErrorMessage>{error.security}</ErrorMessage>}
            
            <form onSubmit={handlePasswordSubmit}>
              <FormGroup>
                <Label>Current Password</Label>
                <Input 
                  type="password" 
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>New Password</Label>
                <Input 
                  type="password" 
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="8"
                />
                <small style={{ color: '#777', marginTop: '5px', display: 'block' }}>
                  Password must be at least 8 characters long
                </small>
              </FormGroup>
              <FormGroup>
                <Label>Confirm New Password</Label>
                <Input 
                  type="password" 
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </FormGroup>
              <Button 
                type="submit"
                disabled={loading.security}
              >
                {loading.security ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </TileContent>
        </Tile>
      </TilesContainer>
      
      {/* Logout button moved to profile section */}
      
      {/* Address Modal */}
      <Modal 
        isOpen={addressModal} 
        onClose={() => setAddressModal(false)} 
        title={editingAddress ? 'Edit Address' : 'Add New Address'}
      >
        <AddressForm 
          initialData={editingAddress || {}} 
          isEditing={!!editingAddress}
          onCancel={() => setAddressModal(false)}
          onSubmit={async (addressData) => {
            try {
              if (editingAddress) {
                await updateAddress(currentUser.uid, editingAddress.id, addressData);
              } else {
                await addAddress(currentUser.uid, addressData);
              }
              
              // Refresh addresses
              const updatedAddresses = await getUserAddresses(currentUser.uid);
              setAddresses(updatedAddresses);
              setAddressModal(false);
              setError(prev => ({ ...prev, addresses: null }));
            } catch (err) {
              console.error('Error saving address:', err);
              setError(prev => ({ 
                ...prev, 
                addresses: `Failed to ${editingAddress ? 'update' : 'add'} address. Please try again.` 
              }));
            }
          }}
        />
      </Modal>
      
      {/* Payment Method Modal */}
      <Modal 
        isOpen={paymentModal} 
        onClose={() => setPaymentModal(false)} 
        title={editingPayment ? 'Edit Payment Method' : 'Add New Payment Method'}
      >
        <PaymentMethodForm 
          initialData={editingPayment || {}} 
          isEditing={!!editingPayment}
          onCancel={() => setPaymentModal(false)}
          onSubmit={async (paymentData) => {
            try {
              if (editingPayment) {
                await updatePaymentMethod(currentUser.uid, editingPayment.id, paymentData);
              } else {
                await addPaymentMethod(currentUser.uid, paymentData);
              }
              
              // Refresh payment methods
              const updatedPaymentMethods = await getUserPaymentMethods(currentUser.uid);
              setPaymentMethods(updatedPaymentMethods);
              setPaymentModal(false);
              setError(prev => ({ ...prev, payment: null }));
            } catch (err) {
              console.error('Error saving payment method:', err);
              setError(prev => ({ 
                ...prev, 
                payment: `Failed to ${editingPayment ? 'update' : 'add'} payment method. Please try again.` 
              }));
            }
          }}
        />
      </Modal>
    </Container>
  );
};

export default Account;
